import React from "react";
import { Sprout, Shield, LineChart, Users, Infinity } from "lucide-react";

/**
 * Phase 10 — 5-pillar brand strip.
 *
 * Grow → Protect → Invest → Enjoy → Legacy
 * Reflects the pillar strip at the bottom of the brand logo.
 * Clickable: each pillar scrolls to an in-page anchor if one exists,
 * otherwise a #pillar-<slug> stub so links don't hard-404.
 */

const PILLARS = [
  {
    slug: "grow",
    label: "Grow",
    desc: "Early-stage savings & accumulation",
    color: "#2E7D32",
    Icon: Sprout,
  },
  {
    slug: "protect",
    label: "Protect",
    desc: "Life, health, long-term care",
    color: "#D4AF37",
    Icon: Shield,
  },
  {
    slug: "invest",
    label: "Invest",
    desc: "Annuities, IUL, retirement income",
    color: "#2E7D32",
    Icon: LineChart,
  },
  {
    slug: "enjoy",
    label: "Enjoy",
    desc: "Retirement lifestyle income",
    color: "#D4AF37",
    Icon: Users,
  },
  {
    slug: "legacy",
    label: "Legacy",
    desc: "Generational wealth transfer",
    color: "#2E7D32",
    Icon: Infinity,
  },
];

export const PillarStrip = ({ variant = "light", onDark = false }) => {
  const labelColor = onDark ? "#FAF8F3" : "#0A2540";
  const descColor = onDark ? "rgba(250,247,242,0.65)" : "#6B7280";
  const divider = onDark ? "rgba(212,175,55,0.3)" : "rgba(10,37,64,0.08)";

  return (
    <div
      data-testid="pillar-strip"
      className="w-full"
      style={{ borderTop: `1px solid ${divider}`, borderBottom: `1px solid ${divider}` }}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-2 sm:grid-cols-5 gap-4 sm:gap-6 py-5 sm:py-6">
        {PILLARS.map(({ slug, label, desc, color, Icon }) => (
          <a
            key={slug}
            href={`#pillar-${slug}`}
            data-testid={`pillar-${slug}`}
            className="flex items-center gap-3 group transition-opacity hover:opacity-100"
            style={{ opacity: 0.92 }}
            aria-label={`Pillar: ${label}`}
          >
            <span
              className="flex items-center justify-center rounded-full flex-shrink-0 transition-transform duration-300 group-hover:scale-110"
              style={{
                background: color + "22",
                border: `1.5px solid ${color}`,
                width: 36,
                height: 36,
              }}
            >
              <Icon size={18} strokeWidth={2} style={{ color }} />
            </span>
            <span className="flex flex-col leading-tight min-w-0">
              <span
                className="font-sans-ui text-[0.72rem] tracking-[0.16em] uppercase font-semibold"
                style={{ color: labelColor }}
              >
                {label}
              </span>
              <span
                className="text-[0.72rem] truncate"
                style={{ color: descColor }}
              >
                {desc}
              </span>
            </span>
          </a>
        ))}
      </div>
    </div>
  );
};

export default PillarStrip;
